import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { Address, PartnerDestination } from '@returnmates/client-core/src/graphql/generated/api'
import { getPartners } from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type/index'
import { PickupDetails } from '@returnmates/client-core/src/type/pickups'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import AddNewAddressModal from '@returnmates/ui-core/src/components/AddNewAddressModal'
import AddressSelectionItem from '@returnmates/ui-core/src/components/AddressSelectionItem'
import { FORM_ERROR } from 'final-form'
import { memo, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import useStyles from '../styles'

interface Props {
  partnerCode?: string
}

function DestinationsSettings({ partnerCode }: Props) {
  const classes = useStyles({})
  const dispatch = useDispatch()
  const partners = useSelector(getPartners)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [currentAddress, setCurrentAddress] = useState<Address | null>()

  const openPartnerDestinationModal = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closePartnerDestinationModal = useCallback(() => {
    setCurrentAddress(null)
    setIsOpen(false)
  }, [])

  const currentPartner = useMemo(() => partners.find(p => p.code === partnerCode), [
    partners,
    partnerCode,
  ])

  const createAddress = useCallback(
    async (val: PickupDetails) => {
      setIsLoading(true)
      try {
        const res: PartnerDestination = await createAsyncAction(
          dispatch,
          actions.createConsolidationAddress.request({
            ...val,
            partnerCode,
            skipServicedArea: true,
          }),
        )

        if (res) {
          closePartnerDestinationModal()
        }
      } catch (err) {
        const { message } = err as Error

        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )

        return { [FORM_ERROR]: errorMapper(message || (err as string)) }
      } finally {
        setIsLoading(false)
      }
    },
    [closePartnerDestinationModal, dispatch, partnerCode],
  )

  const editAddress = useCallback((addr: Address) => {
    setCurrentAddress({ ...addr })
    setIsOpen(true)
  }, [])

  const updateAddress = useCallback(
    async (val: PickupDetails & { ignoreGeocoding: boolean }) => {
      if (val.id) {
        setIsLoading(true)
        try {
          await createAsyncAction(
            dispatch,
            actions.updateAddress.request({
              id: val.id,
              input: {
                name: val.name,
                phone: val.phone,
                street: val.street,
                unit: val.unit,
                city: val.city,
                state: val.state ?? '',
                zipCode: val.zipCode,
                instructions: val.instructions,
                nickname: val.nickname,
                isDefault: val.isDefault,
                geocode: !val.ignoreGeocoding,
              },
              skipServicedArea: true,
            }),
          )

          closePartnerDestinationModal()
        } catch (err) {
          const { message } = err as Error

          return { [FORM_ERROR]: errorMapper(message || (err as string)) }
        } finally {
          setIsLoading(false)
        }
      } else {
        await createAddress(val)
      }
    },
    [dispatch, closePartnerDestinationModal, createAddress],
  )

  const removeAddress = useCallback(
    async (payload: { id: string; partnerDestinationId: string }) => {
      setIsLoading(true)
      try {
        await createAsyncAction(
          dispatch,
          actions.deleteConsolidationAddress.request({
            addressId: payload.id,
            partnerDestinationId: payload.partnerDestinationId,
          }),
        )
      } catch (err) {
        const { message } = err as Error

        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch],
  )

  return (
    <>
      {currentPartner?.destinations?.map(destination =>
        destination ? (
          <AddressSelectionItem
            address={destination.address}
            destinationId={destination.id}
            key={destination?.id}
            editAddress={editAddress}
            startRemove={removeAddress}
            showRemoveIcon
            addressItemStyles={classes.partnerDestination}
          />
        ) : null,
      )}
      <div className={classes.addPrice} onClick={openPartnerDestinationModal}>
        + add a new destination
      </div>
      <AddNewAddressModal
        isOpen={isOpen}
        onClose={closePartnerDestinationModal}
        onAddressUpdate={updateAddress}
        addressToUpdate={currentAddress}
        isLoading={isLoading}
        contactDetailsLabel="Contact details"
        addressDetailsLabel="Pickup address"
        showIgnoreGeocode
        addressSubLabel=""
        isZipCodeWithoutValidation
      />
    </>
  )
}

export default memo(DestinationsSettings)
